import { useEffect, useMemo, useState } from "react";

import { useSearchAdminPermissions } from "@/search/NewSearchPageMVP/provider/hooks";
import { useSearchPageContext } from "@/search/NewSearchPageMVP/provider/SearchPageProvider";
import { useStandardsSearchContext } from "@/search/NewSearchPageMVP/provider/StandardsSearchProvider";
import { AgathaSchemaParamsEnum } from "@/search/NewSearchPageMVP/types/IParametersSchema";
import {
  MAX_FILTERS_DISPLAY_COLLAPSED,
  NO_CLEAR_FILTERS,
  NO_SHOW_FILTERS,
} from "../FilterControls.constants";
import {
  collectTaxonomySlugs,
  removeKeyFromTaxonomyTags,
} from "../../filters/Topics/TaxonomyTags/utils/processCategoryData";
import { controlRemoveTaxonomyTag } from "../../filters/Topics/TaxonomyTags/TaxonomyTags.constants";

export const useFilterControls = () => {
  const [showAllSelectedFilters, setShowAllSelectedFilters] = useState(false);
  const [allowCollapseFilters, setAllowCollapseFilters] = useState(false);
  const { blockActionAndShowConfirmationModal, blockFilterChanges } =
    useSearchAdminPermissions();
  const { setSelectedStandards } = useStandardsSearchContext();

  const {
    getters: { selectedTaxonomyTags },
    handlers: {
      getAllSearchParams,
      toggleSearchParamValue,
      deleteSearchParamValues,
      setSelectedTaxonomyTags,
      setTaxonomyTagsSlugs,
      setSearchParamValue,
    },
  } = useSearchPageContext();

  const allSearchParams = getAllSearchParams();
  const filterChipsToDisplay = allSearchParams
    .filter(([paramName]) => !NO_SHOW_FILTERS.includes(paramName))
    .reduce<Array<[paramName: string, paramValue: string]>>(
      (acc, [paramName, paramValue]) => {
        if (paramValue) {
          const paramValues = paramValue.split(",");
          paramValues.forEach((paramValue) => {
            acc.push([paramName, paramValue]);
            return acc;
          });
        }
        return acc;
      },
      [],
    );

  const handleClearAllFilters = () => {
    if (blockFilterChanges) {
      blockActionAndShowConfirmationModal();
      return;
    }

    const hasTaxonomyTagsFilter = allSearchParams
      .flat()
      .find((filter) => filter === AgathaSchemaParamsEnum.taxonomy_tags);
    if (hasTaxonomyTagsFilter) {
      setSelectedTaxonomyTags({});
      setTaxonomyTagsSlugs([]);
      deleteSearchParamValues(AgathaSchemaParamsEnum.taxonomy_tags);
    }
    allSearchParams.forEach(([paramName]) => {
      if (NO_CLEAR_FILTERS.includes(paramName)) return;
      if (paramName === AgathaSchemaParamsEnum.related_to_standards)
        setSelectedStandards([]);
      deleteSearchParamValues(paramName);
    });
  };

  const handleRemoveFilter = (_: unknown, value: string) => {
    const [paramName, paramValue] = value.split(",") as [
      paramName: string,
      paramValue: string,
    ];

    if (paramName === AgathaSchemaParamsEnum.related_to_standards) {
      setSelectedStandards((prev) =>
        prev.filter((standard) => standard.content_id !== paramValue),
      );
    }

    if (paramName === AgathaSchemaParamsEnum.taxonomy_tags) {
      toggleSearchParamValue(controlRemoveTaxonomyTag, "true");
      const updatedSelectedTaxonomyTags = removeKeyFromTaxonomyTags(
        selectedTaxonomyTags,
        paramValue,
      );
      setSelectedTaxonomyTags(updatedSelectedTaxonomyTags);
      deleteSearchParamValues(AgathaSchemaParamsEnum.taxonomy_tags);
      const taxonomyTagsSlugs = collectTaxonomySlugs(
        updatedSelectedTaxonomyTags,
      );
      setTaxonomyTagsSlugs(taxonomyTagsSlugs);
      if (taxonomyTagsSlugs.length > 0) {
        setSearchParamValue(
          AgathaSchemaParamsEnum.taxonomy_tags,
          taxonomyTagsSlugs.join(","),
        );
      } else {
        deleteSearchParamValues(AgathaSchemaParamsEnum.taxonomy_tags);
      }
    } else {
      toggleSearchParamValue(paramName, paramValue);
    }
  };

  const handleToggleShowFilters = () => {
    setShowAllSelectedFilters(!showAllSelectedFilters);
  };

  const filteredChipsToDisplay = useMemo(() => {
    return showAllSelectedFilters
      ? filterChipsToDisplay
      : filterChipsToDisplay.slice(0, MAX_FILTERS_DISPLAY_COLLAPSED);
  }, [showAllSelectedFilters, filterChipsToDisplay]);

  useEffect(() => {
    if (
      filterChipsToDisplay.length > MAX_FILTERS_DISPLAY_COLLAPSED &&
      !allowCollapseFilters
    ) {
      setAllowCollapseFilters(true);
    } else if (
      filterChipsToDisplay.length <= MAX_FILTERS_DISPLAY_COLLAPSED &&
      allowCollapseFilters
    ) {
      setAllowCollapseFilters(false);
    }
  }, [filteredChipsToDisplay]);

  return {
    allowCollapseFilters,
    filteredChipsToDisplay,
    showAllSelectedFilters,
    handleClearAllFilters,
    handleRemoveFilter,
    handleToggleShowFilters,
  };
};
